import React, { useState, useEffect } from 'react';
import { Link2, Database, Send, Copy, ExternalLink, RefreshCw, Plus, Trash2 } from 'lucide-react';
import { StepByStepWrapper } from './StepByStepWrapper';

// D1 API is now deployed!
const API_URL = import.meta.env.PROD
  ? 'https://happyhumantools-d1-api.b-9f2.workers.dev'
  : 'https://happyhumantools-d1-api.b-9f2.workers.dev'; // Using production API for both dev and prod

interface UploadLink {
  id: string;
  slug: string;
  name: string;
  description?: string;
  created_by: string;
  expires_at: string;
  max_files: number;
  max_size_mb: number;
  files_uploaded: number;
  status: string;
  upload_url?: string;
  created_at: string;
}

export const UploadLinkManager: React.FC = () => {
  const [links, setLinks] = useState<UploadLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [useMockData, setUseMockData] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    createdBy: '',
    expiresInDays: 7,
    maxFiles: 10,
    maxSizeMb: 100
  });

  // Step tracking for visualization
  const [steps, setSteps] = useState<any[]>([]);

  const updateStep = (stepId: string, update: any) => {
    setSteps(prev => prev.map(s => s.id === stepId ? { ...s, ...update } : s));
  };

  const resetSteps = () => {
    setSteps([
      { id: 'init', label: 'Initialize Request', status: 'pending' },
      { id: 'api', label: 'Call D1 API', status: 'pending' },
      { id: 'database', label: 'Database Operation', status: 'pending' },
      { id: 'response', label: 'Process Response', status: 'pending' },
      { id: 'update', label: 'Update UI', status: 'pending' }
    ]);
  };

  // Fetch links with step tracking
  const fetchLinks = async () => {
    resetSteps();
    setLoading(true);
    setError(null);

    const startTime = Date.now();
    updateStep('init', { status: 'completed', timestamp: new Date().toISOString() });

    try {
      updateStep('api', { status: 'active', timestamp: new Date().toISOString() });

      let data: UploadLink[];

      try {
        // Try real API first
        const response = await fetch(`${API_URL}/api/upload-links`);
        if (!response.ok) throw new Error('API not available');

        const result = await response.json();
        data = result.data || [];
        setUseMockData(false);
      } catch (apiError) {
        // Fallback to mock data
        console.log('Using mock data (D1 not available)');
        setUseMockData(true);

        // Get from localStorage or use defaults
        const stored = localStorage.getItem('mock_upload_links');
        data = stored ? JSON.parse(stored) : [
          {
            id: 'mock-1',
            slug: 'demo123',
            name: 'Demo Upload Link',
            description: 'Example link for testing',
            created_by: 'demo@example.com',
            expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
            max_files: 10,
            max_size_mb: 100,
            files_uploaded: 0,
            status: 'active',
            upload_url: '/upload/demo123',
            created_at: new Date().toISOString()
          }
        ];
      }

      updateStep('api', {
        status: 'completed',
        duration: Date.now() - startTime,
        data: { recordCount: data.length }
      });

      updateStep('database', {
        status: 'completed',
        timestamp: new Date().toISOString(),
        data: { table: 'upload_links', operation: 'SELECT' }
      });

      updateStep('response', {
        status: 'active',
        timestamp: new Date().toISOString()
      });

      setLinks(data);

      updateStep('response', {
        status: 'completed',
        data: { links: data.length }
      });

      updateStep('update', {
        status: 'completed',
        timestamp: new Date().toISOString(),
        duration: Date.now() - startTime
      });

    } catch (err) {
      console.error('Error fetching links:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      updateStep('api', { status: 'error', data: { error: errorMessage } });
    } finally {
      setLoading(false);
    }
  };

  // Create link with step tracking
  const createLink = async (e: React.FormEvent) => {
    e.preventDefault();
    resetSteps();
    setLoading(true);
    setError(null);

    const startTime = Date.now();
    updateStep('init', {
      status: 'completed',
      timestamp: new Date().toISOString(),
      data: formData
    });

    try {
      updateStep('api', { status: 'active', timestamp: new Date().toISOString() });

      let newLink: UploadLink;

      if (useMockData) {
        // Mock creation
        const slug = Math.random().toString(36).substring(7);
        newLink = {
          id: `mock-${Date.now()}`,
          slug,
          name: formData.name,
          description: formData.description,
          created_by: formData.createdBy,
          expires_at: new Date(Date.now() + formData.expiresInDays * 24 * 60 * 60 * 1000).toISOString(),
          max_files: formData.maxFiles,
          max_size_mb: formData.maxSizeMb,
          files_uploaded: 0,
          status: 'active',
          upload_url: `/upload/${slug}`,
          created_at: new Date().toISOString()
        };

        // Save to localStorage
        const stored = localStorage.getItem('mock_upload_links');
        const existing = stored ? JSON.parse(stored) : [];
        existing.push(newLink);
        localStorage.setItem('mock_upload_links', JSON.stringify(existing));
      } else {
        // Real API call
        const response = await fetch(`${API_URL}/api/upload-links`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        });

        if (!response.ok) throw new Error('Failed to create link');

        const result = await response.json();
        newLink = result.data;
      }

      updateStep('api', {
        status: 'completed',
        duration: Date.now() - startTime
      });

      updateStep('database', {
        status: 'completed',
        timestamp: new Date().toISOString(),
        data: {
          table: 'upload_links',
          operation: 'INSERT',
          slug: newLink.slug
        }
      });

      updateStep('response', {
        status: 'completed',
        data: newLink
      });

      setLinks(prev => [newLink, ...prev]);
      setShowCreateForm(false);
      setFormData({
        name: '',
        description: '',
        createdBy: '',
        expiresInDays: 7,
        maxFiles: 10,
        maxSizeMb: 100
      });

      updateStep('update', {
        status: 'completed',
        timestamp: new Date().toISOString(),
        duration: Date.now() - startTime
      });

    } catch (err) {
      console.error('Error creating link:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      updateStep('api', { status: 'error', data: { error: errorMessage } });
    } finally {
      setLoading(false);
    }
  };

  // Delete link
  const deleteLink = async (id: string) => {
    if (!confirm('Are you sure you want to delete this link?')) return;

    try {
      if (useMockData) {
        // Mock deletion
        const stored = localStorage.getItem('mock_upload_links');
        const existing = stored ? JSON.parse(stored) : [];
        const filtered = existing.filter((l: UploadLink) => l.id !== id);
        localStorage.setItem('mock_upload_links', JSON.stringify(filtered));
      } else {
        // Real API call
        const response = await fetch(`${API_URL}/api/upload-links/${id}`, {
          method: 'DELETE'
        });
        if (!response.ok) throw new Error('Failed to delete link');
      }

      setLinks(prev => prev.filter(l => l.id !== id));
    } catch (err) {
      console.error('Error deleting link:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
    }
  };

  // Copy link
  const copyLink = (link: UploadLink) => {
    // If upload_url is already absolute, use it directly
    const fullUrl = link.upload_url?.startsWith('http')
      ? link.upload_url
      : `${window.location.origin}/upload/${link.slug}`;
    navigator.clipboard.writeText(fullUrl);
    alert(`Copied: ${fullUrl}`);
  };

  useEffect(() => {
    fetchLinks();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Database className="w-8 h-8 text-indigo-600" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Upload Link Manager</h1>
                <p className="text-gray-600 mt-1">D1 Database Integration Demo</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {useMockData && (
                <div className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium">
                  Mock Mode (D1 not deployed)
                </div>
              )}
              <button
                onClick={fetchLinks}
                className="p-2 text-gray-600 hover:text-indigo-600 transition"
                disabled={loading}
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={() => setShowCreateForm(true)}
                className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition"
              >
                <Plus className="w-5 h-5" />
                Create Link
              </button>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
              {error}
            </div>
          )}

          {/* Create Form */}
          {showCreateForm && (
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Upload Link</h3>
              <form onSubmit={createLink} className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Link Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Your Email
                  </label>
                  <input
                    type="email"
                    value={formData.createdBy}
                    onChange={(e) => setFormData({ ...formData, createdBy: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description (Optional)
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                    rows={2}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Expires In (Days)
                  </label>
                  <input
                    type="number"
                    value={formData.expiresInDays}
                    onChange={(e) => setFormData({ ...formData, expiresInDays: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                    min="1"
                    max="365"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Files
                  </label>
                  <input
                    type="number"
                    value={formData.maxFiles}
                    onChange={(e) => setFormData({ ...formData, maxFiles: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                    min="1"
                  />
                </div>
                <div className="col-span-2 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {loading ? 'Creating...' : 'Create Link'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Links List */}
          <div className="space-y-4">
            {links.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Database className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No upload links yet. Create your first one!</p>
              </div>
            ) : (
              links.map((link) => (
                <div key={link.id} className="border rounded-lg p-4 hover:shadow-md transition">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <Link2 className="w-5 h-5 text-indigo-600" />
                        <h3 className="font-semibold text-gray-900">{link.name}</h3>
                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium">
                          {link.status}
                        </span>
                      </div>
                      {link.description && (
                        <p className="text-gray-600 text-sm mt-1">{link.description}</p>
                      )}
                      <div className="flex items-center gap-6 mt-2 text-sm text-gray-500">
                        <span>Slug: <code className="bg-gray-100 px-1 rounded">{link.slug}</code></span>
                        <span>Files: {link.files_uploaded}/{link.max_files}</span>
                        <span>Expires: {new Date(link.expires_at).toLocaleDateString()}</span>
                        <span>Created by: {link.created_by}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => copyLink(link)}
                        className="p-2 text-gray-600 hover:text-indigo-600 transition"
                        title="Copy link"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <a
                        href={link.upload_url?.startsWith('http') ? link.upload_url : `/upload/${link.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-gray-600 hover:text-indigo-600 transition"
                        title="Open link"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                      <button
                        onClick={() => deleteLink(link.id)}
                        className="p-2 text-gray-600 hover:text-red-600 transition"
                        title="Delete link"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Step by Step Visualization */}
        <StepByStepWrapper steps={steps} onStepUpdate={updateStep} />

        {/* Instructions */}
        <div className="bg-white rounded-xl shadow-lg p-8 mt-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">How This Works</h2>
          <div className="space-y-3 text-gray-600">
            <div className="flex gap-3">
              <span className="font-bold">1.</span>
              <div>
                <strong>D1 Database:</strong> CloudFlare's edge SQLite database stores upload link metadata
              </div>
            </div>
            <div className="flex gap-3">
              <span className="font-bold">2.</span>
              <div>
                <strong>CloudFlare Worker:</strong> API endpoint handles CRUD operations for the upload_links table
              </div>
            </div>
            <div className="flex gap-3">
              <span className="font-bold">3.</span>
              <div>
                <strong>Step Visualization:</strong> See exactly how data flows through each component in real-time
              </div>
            </div>
            <div className="flex gap-3">
              <span className="font-bold">4.</span>
              <div>
                <strong>Mock Mode:</strong> When D1 isn't deployed, falls back to localStorage for testing
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-yellow-50 rounded-lg">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> To deploy D1, run these commands in the workers/d1-api directory:
            </p>
            <pre className="mt-2 text-xs bg-gray-900 text-gray-100 p-3 rounded overflow-x-auto">
{`# Create D1 database
npx wrangler d1 create happyhumantools

# Apply schema
npx wrangler d1 execute happyhumantools --file=../d1-schema.sql

# Update wrangler.toml with database_id
# Deploy worker
npx wrangler deploy`}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
};